<template>
  <Modal :showing="isOpen" :min-width="true" @close="closeModal" class="">
    <div class="space-y-4" v-if="hasSizeData">
      <h3 class="text-gray-900">
        {{ productReference.sizeTableHeading || $t('sizeChart.title') }}
      </h3>

      <div
        v-if="productReference.sizeTableIntroduction"
        class="text-gray-600"
        v-html="productReference.sizeTableIntroduction"
      ></div>

      <div v-if="productSizeTable.length > 0">
        <div class="overflow-x-auto relative">
          <table class="min-w-full" role="table" :aria-label="$t('sizeChart.title')">
            <thead class="bg-putty">
              <tr role="row">
                <th
                  v-for="(header, index) in tableHeaders"
                  :key="`header-${header.handle}`"
                  role="columnheader"
                  scope="col"
                  class="px-3 py-3 text-center text-sm font-medium tracking-wider"
                  :class="{ 'left-0 z-10 bg-putty': index === 0 }"
                >
                  {{ header.value }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white">
              <tr v-for="(row, rowIndex) in tableRows" :key="`row-${rowIndex}`" role="row">
                <template v-for="(cell, cellIndex) in row" :key="`cell-${rowIndex}-${cellIndex}`">
                  <th
                    v-if="cellIndex === 0"
                    role="rowheader"
                    scope="row"
                    class="px-3 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-900 bg-putty left-0 z-10"
                  >
                    {{ cell }}
                  </th>
                  <td v-else role="gridcell" class="px-3 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                    {{ cell }}
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <section
        v-if="productReference.sizeTableExplanation || productReference.sizeTableVisual"
        class="pt-6"
        aria-labelledby="measurement-guide-heading"
      >
        <div class="flex flex-col md:flex-row gap-6">
          <div v-if="productReference.sizeTableExplanation" class="w-full">
            <div class="text-sm" role="text" v-html="productReference.sizeTableExplanation"></div>
          </div>
          <div
            v-if="productReference.sizeTableVisual && productReference.sizeTableVisual[0]"
            class="flex justify-center w-full"
          >
            <img
              :src="productReference.sizeTableVisual[0].url"
              :alt="productReference.sizeTableVisual[0].alt"
              class="max-w-full h-auto rounded"
              role="img"
            />
          </div>
        </div>
      </section>
    </div>
  </Modal>
</template>

<script>
import Modal from '@/components/common/Modal';

export default defineNuxtComponent({
  name: 'SizeTableModal',

  components: {
    Modal,
  },

  props: {
    showing: {
      type: Boolean,
      default: false,
    },
    productReference: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['close'],

  data() {
    return {};
  },

  computed: {
    isOpen() {
      return this.showing;
    },

    hasSizeData() {
      return this.productReference && this.productReference.productSizeTable?.length >= 2;
    },

    productSizeTable() {
      return this.productReference.productSizeTable || [];
    },

    tableHeaders() {
      if (!this.productSizeTable || this.productSizeTable.length === 0) return [];

      const firstRow = this.productSizeTable[0];

      // Special handling for getting size header column
      const headers = [{ handle: 'size', value: firstRow.size || this.$t('sizeChart.size') }];

      return [
        ...headers,
        ...Object.keys(firstRow)
          .filter((key) => key.startsWith('col') && firstRow[key])
          .map((key) => {
            return { handle: key, value: firstRow[key] };
          }),
      ];
    },

    tableRows() {
      if (!this.productSizeTable || this.productSizeTable.length <= 1) return [];

      return this.productSizeTable.slice(1).map((row) => {
        return this.tableHeaders.map((header) => row[header.handle] || '');
      });
    },
  },

  methods: {
    closeModal() {
      this.$emit('close');
    },
  },
});
</script>
